/**
 * main-readout.ts — live-value resolver for the /main Summary readout tab.
 *
 * Turns the {{token}} placeholders authored in main-readout-content.ts into
 * formatted live values from the /api/main/scorecard payload. See that file's
 * header for the token grammar. Every path is defensive: any missing/null slice
 * resolves to an em dash so the readout never fabricates a number (the same
 * discipline the tiles use).
 *
 * CRITICAL — the readout must read IDENTICALLY to the tiles, so each token kind
 * mirrors the EXACT formatter its tile uses (verified against MainTrendTile,
 * MainBucketedTile, MainPacingTile):
 *   - MAIN-12 headline uses formatCompactCurrencyK ("$82k")  → kind `qtrUsd`
 *   - MAIN-20 headline uses `${v.toFixed(1)} days`           → kind `days`
 *   - generic bucketed headline uses formatTotal (≥1M ? $ : int) → kind `total`
 *   - count activity headlines use formatCountInt            → kind `count`
 *   - MAIN-04 WoW/MoM pills show the ABSOLUTE count change, not a %  → wow/mom
 *   - MAIN-24 "% of target" uses formatPercentToTarget (toFixed(0)) → pctTarget
 *   - MAIN-17 FY figures mirror MainPacingTile (net of Closed-Won) → fy* kinds
 */

import { money, pct as pctFmt, rate } from './exec/fmt.js';
import { FY2027_PIPELINE_TARGET } from './exec/links.js';
import { rilletFrom } from './exec/rillet-source.js';
import { formatCompactCurrencyK, formatCountInt, formatCurrency } from './format.js';
import { computeSnapDelta } from './snap-delta.js';

import type { ExecResponse, ExecRilletPanel, MainMetricResponse, MainScorecardResponse } from '@saledash/shared';

const DASH = '—';

type Unit = MainMetricResponse['unit'];

/** Unit-aware single-value formatter — mirrors MainTrendTile.formatMainValue. */
function formatByUnit(value: number, unit: Unit): string {
  switch (unit) {
    case 'USD':
      return formatCurrency(value);
    case 'percent':
      return `${(value * 100).toFixed(1)}%`;
    case 'days':
      return `${value.toFixed(1)} days`;
    case 'hours':
      return `${value.toFixed(1)} hours`;
    case 'ratio':
      return value.toFixed(2);
    default:
      return formatCountInt(value);
  }
}

/**
 * Generic bucketed-tile headline formatter — mirrors MainBucketedTile.formatTotal
 * ($ at ≥ $1M, thousands-separated integer below). Used by MAIN-32 etc.
 */
function formatTotalLikeTile(value: number): string {
  if (value >= 1_000_000) return formatCurrency(value);
  return formatCountInt(value);
}

/**
 * WoW/MoM change EXACTLY as MainBucketedTile.ActivityDeltaPill renders it — the
 * ABSOLUTE count difference ("up 120" / "down 80" / "flat"), NOT a percentage,
 * so the readout can't disagree with the tile's pill.
 */
function formatCountDelta(pair: { current: number; prior: number } | undefined): string {
  if (!pair) return DASH;
  const diff = pair.current - pair.prior;
  if (diff === 0) return 'flat';
  return `${diff > 0 ? 'up' : 'down'} ${formatCountInt(Math.abs(diff))}`;
}

/** "% of target" mirroring fiscal.formatPercentToTarget (toFixed(0)); null-safe. */
function formatPctOfTarget(value: number | null, target: number | null): string {
  if (value == null || target == null || target === 0) return DASH;
  return `${((value / target) * 100).toFixed(0)}%`;
}

/**
 * MAIN-17 pacing summary — mirrors MainPacingTile's derivation so the readout
 * shows the SAME headline actual + coverage target the tile leads with.
 *
 * The FY numbers come from the MAIN-48 pacing series buckets ({ cumulative,
 * fyTarget, adjustedTarget, closedWonFy }); the coverage target is net of
 * Closed-Won when the series carries `adjustedTarget` (falling back to the gross
 * fyTarget, then metrics['MAIN-17'].target_value). Coverage % uses Math.round to
 * match MainPacingTile's own rounding.
 */
export interface Main17Pacing {
  actual: number | null;
  coverageTarget: number | null;
  grossTarget: number | null;
  closedWonFy: number;
  netOfClosedWon: boolean;
  pct: number | null;
}

function num(b: Record<string, unknown>, key: string): number {
  const v = b[key];
  return typeof v === 'number' ? v : 0;
}

export function computeMain17Pacing(scorecard: MainScorecardResponse): Main17Pacing {
  const series = scorecard.grouped['MAIN-48'];
  const metric = scorecard.metrics['MAIN-17'];
  const buckets = series?.buckets ?? [];

  let grossFromSeries = 0;
  let adjusted = 0;
  let closedWonFy = 0;
  let lastCumulative = 0;
  for (const b of buckets) {
    if (typeof b.label !== 'string' || b.label === '') continue;
    if (grossFromSeries === 0 && num(b, 'fyTarget') > 0) grossFromSeries = num(b, 'fyTarget');
    if (adjusted === 0 && num(b, 'adjustedTarget') > 0) adjusted = num(b, 'adjustedTarget');
    if (closedWonFy === 0 && num(b, 'closedWonFy') > 0) closedWonFy = num(b, 'closedWonFy');
    lastCumulative = num(b, 'cumulative');
  }

  const grossTarget = grossFromSeries > 0 ? grossFromSeries : (metric?.target_value ?? null);
  const netOfClosedWon = adjusted > 0;
  const coverageTarget = netOfClosedWon ? adjusted : grossTarget;
  const actual = series?.total_value ?? (lastCumulative || (metric?.value ?? null));
  const pct =
    coverageTarget != null && coverageTarget > 0 && actual != null
      ? Math.round((actual / coverageTarget) * 100)
      : null;

  return { actual, coverageTarget, grossTarget, closedWonFy, netOfClosedWon, pct };
}

const PACING_KINDS = new Set([
  'fyActual',
  'fyTarget',
  'fyGrossTarget',
  'fyCoverage',
  'fyClosedWon',
  'fyPaceWord',
]);

/**
 * Resolve one `ID|kind` token spec against the live scorecard.
 * `kind` defaults to `value`. Unknown data → em dash.
 */
export function resolveToken(
  scorecard: MainScorecardResponse | undefined,
  spec: string,
  exec?: ExecResponse,
): string {
  if (spec.startsWith('RILLET|')) return resolveRilletToken(spec.slice(7), rilletFrom(exec));
  if (spec.startsWith('EXEC|')) return resolveExecToken(exec, spec.slice(5));
  if (!scorecard) return DASH;
  const parts = spec.split('|');
  const id = parts[0] ?? '';
  const kind = parts[1] ?? 'value';

  // MAIN-17 FY pacing kinds are derived from the MAIN-48 series (id-independent).
  if (PACING_KINDS.has(kind)) {
    const p = computeMain17Pacing(scorecard);
    switch (kind) {
      case 'fyActual':
        return p.actual != null ? formatCurrency(p.actual) : DASH;
      case 'fyTarget':
        return p.coverageTarget != null ? formatCurrency(p.coverageTarget) : DASH;
      case 'fyGrossTarget':
        return p.grossTarget != null ? formatCurrency(p.grossTarget) : DASH;
      case 'fyCoverage':
        return p.pct != null ? `${String(p.pct)}%` : DASH;
      case 'fyClosedWon':
        return p.closedWonFy > 0 ? formatCurrency(p.closedWonFy) : DASH;
      case 'fyPaceWord':
        // Coverage-based (not time-prorated): the coverage target is a full-FY
        // figure, so this reads "above/below the coverage target", never "pace".
        return p.pct != null
          ? (p.pct >= 100 ? 'above the coverage target' : 'below the coverage target')
          : DASH;
      default:
        return DASH;
    }
  }

  const metric = scorecard.metrics[id];
  const grouped = scorecard.grouped[id];

  switch (kind) {
    case 'value':
      return metric?.value != null ? formatByUnit(metric.value, metric.unit) : DASH;
    case 'target':
      return metric?.target_value != null
        ? formatByUnit(metric.target_value, metric.unit)
        : DASH;
    case 'pctTarget':
      return formatPctOfTarget(metric?.value ?? null, metric?.target_value ?? null);
    case 'count':
      return grouped?.total_value != null ? formatCountInt(grouped.total_value) : DASH;
    case 'total':
      // Generic bucketed headline (MAIN-32 etc.) — mirrors MainBucketedTile.formatTotal.
      return grouped?.total_value != null ? formatTotalLikeTile(grouped.total_value) : DASH;
    case 'qtrUsd':
      // Unit-quarter currency headline (MAIN-12) — mirrors formatCompactCurrencyK.
      return grouped?.total_value != null ? formatCompactCurrencyK(grouped.total_value) : DASH;
    case 'days':
      return grouped?.total_value != null
        ? `${grouped.total_value.toFixed(1)} days`
        : DASH;
    case 'groupedPctTarget':
      return formatPctOfTarget(grouped?.total_value ?? null, grouped?.target_value ?? null);
    case 'wow':
      return formatCountDelta(grouped?.deltas?.wow);
    case 'mom':
      return formatCountDelta(grouped?.deltas?.mom);
    case 'trend': {
      // Direction from real snapshot history (snap-backed metrics only). The
      // percentage mirrors MainTrendTile's WoW chip (round(abs(ratio)*100)).
      const delta = computeSnapDelta(scorecard.snap[id]?.snapshots ?? []);
      if (!delta) return DASH;
      if (delta.flat) return 'flat on the week';
      const pct = Math.round(Math.abs(delta.ratio) * 100);
      return `${delta.up ? 'up' : 'down'} ${String(pct)}% on the week`;
    }
    default:
      return DASH;
  }
}

const TOKEN_RE = /\{\{([^}]+)\}\}/g;

/** Replace every {{token}} in `text` with its resolved live value. */
export function resolveText(
  scorecard: MainScorecardResponse | undefined,
  text: string,
  exec?: ExecResponse,
): string {
  return text.replace(TOKEN_RE, (_match, spec: string) => resolveToken(scorecard, spec.trim(), exec));
}

/**
 * quick-261104-ia3 — Rillet tokens (dated constant lib/exec/rillet-arr.ts):
 *   RILLET|arr · arrYoY · churn12 · churn12Pct · lastChurn · lastChurnMonth ·
 *   nrr · grr · renewalsOpen90
 */
export function resolveRilletToken(kind: string, R: ExecRilletPanel): string {
  const last = R.months[R.months.length - 1];
  const yearAgo = R.months[R.months.length - 13];
  const months = R.months.slice(-12);
  const churn = months.reduce((s, m) => s + m.churnArr + m.contractionArr, 0);
  const exp = months.reduce((s, m) => s + m.expansionArr, 0);
  const start = yearAgo?.arr ?? 0;
  switch (kind) {
    case 'arr':
      return last ? money(last.arr) : DASH;
    case 'arrYoY':
      return last && start ? `${last.arr >= start ? 'up' : 'down'} ${pctFmt(Math.abs(last.arr - start) / start)}` : DASH;
    case 'churn12':
      return money(churn);
    case 'churn12Pct':
      return start ? pctFmt(churn / start) : DASH;
    case 'lastChurn':
      return last ? money(last.churnArr) : DASH;
    case 'lastChurnMonth':
      return last ? `${last.month}${last.month === R.provisionalMonth ? ' (provisional)' : ''}` : DASH;
    case 'nrr':
      return start ? pctFmt((start - churn + exp) / start) : DASH;
    case 'grr':
      return start ? pctFmt((start - churn) / start) : DASH;
    case 'renewalsOpen90': {
      const today = new Date().toISOString().slice(0, 10);
      const end = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
      const open = R.renewals.filter((r) => !r.renewed && r.endDate >= today && r.endDate <= end);
      return `${String(open.length)} contracts (${money(open.reduce((s, r) => s + r.value, 0))})`;
    }
    default:
      return DASH;
  }
}

/**
 * quick-261104-ia3 — exec-panel tokens (GET /api/main/exec):
 *   EXEC|pipelineOpen · potentialCount · potentialValue · nextYearPipe ·
 *   nextYearPct · nlWin · expWin
 */
export function resolveExecToken(exec: ExecResponse | undefined, kind: string): string {
  if (!exec) return DASH;
  const funnel = exec.panels.funnel?.payload;
  const wr = exec.panels.winrate?.payload;
  const cov = exec.panels.coverage?.payload;
  const nextYear = new Date().getFullYear() + 1;
  const nextYearPipe = cov ? cov.cells.filter((c) => c.year === nextYear).reduce((s, c) => s + c.open, 0) : null;
  switch (kind) {
    case 'pipelineOpen':
      return funnel ? money(Object.values(funnel.current.byStage).reduce((s, v) => s + v, 0)) : DASH;
    case 'potentialCount':
      return funnel ? String(funnel.potential.length) : DASH;
    case 'potentialValue':
      return funnel && exec.avgDealSize != null ? money(funnel.potential.length * exec.avgDealSize) : DASH;
    case 'nextYearPipe':
      return nextYearPipe != null ? money(nextYearPipe) : DASH;
    case 'nextYearPct':
      return nextYearPipe != null ? pctFmt(nextYearPipe / FY2027_PIPELINE_TARGET) : DASH;
    case 'nlWin':
      return wr ? pctFmt(rate(wr.ytd.newWon, wr.ytd.newLost)) : DASH;
    case 'expWin':
      return wr ? pctFmt(rate(wr.ytd.expWon, wr.ytd.expLost)) : DASH;
    default:
      return DASH;
  }
}
